<template>
  <div v-if="isEnabled">
    <div v-if="showDivider" class="flex items-center gap-3 my-1">
      <hr class="flex-1 border-gray-300" />
      <span class="text-sm text-gray-500">{{
        $t("login.orContinueWith")
      }}</span>
      <hr class="flex-1 border-gray-300" />
    </div>

    <div class="relative flex items-center justify-center">
      <JoszakiButton
        v-if="useAuthCode"
        type="default"
        outline
        inverted
        expanded
        :loading="loading"
        @click="onButtonClick"
      >
        <svg
          class="h-5 w-5 flex-shrink-0"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 48 48"
          aria-hidden="true"
        >
          <path
            fill="#FFC107"
            d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
          />
          <path
            fill="#FF3D00"
            d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
          />
          <path
            fill="#4CAF50"
            d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
          />
          <path
            fill="#1976D2"
            d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.002-.001.003-.002.005-.003l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
          />
        </svg>
        <span>{{ label || $t("login.continueWithGoogle") }}</span>
      </JoszakiButton>

      <div v-if="!useAuthCode" ref="googleButton" />
    </div>

    <div v-if="error" class="mt-2">
      <JoszakiPill type="warning" class="text-xs">{{ error }}</JoszakiPill>
    </div>
  </div>
</template>

<script>
import {
  onMounted,
  ref,
  nextTick,
  useContext,
  computed,
} from "@nuxtjs/composition-api";
import { useGoogleLogin } from "~/components/_refactored/auth/composables/useGoogleLogin";
import useFeatures, { FEATURES } from "~/composables/useFeatures";

export default {
  name: "GoogleLoginButton",
  props: {
    /**
     * When false, the user stays on the current page after login and a
     * `success` event is emitted instead of navigating. Used for dialog login.
     */
    navigateOnSuccess: {
      type: Boolean,
      default: true,
    },
    /**
     * Request the Google phone scope so a registering user's phone number is
     * fetched and prefilled. Set on registration entry points only — it shows
     * Google's consent screen, which we keep off the login path.
     */
    requestPhone: {
      type: Boolean,
      default: false,
    },
    /**
     * Label for the custom (authorization code) button. Defaults to the
     * "Continue with Google" copy. Ignored by the ID-token path, where Google
     * renders its own button text.
     */
    label: {
      type: String,
      default: "",
    },
    /**
     * Whether to show the "or continue with" divider above the button.
     */
    showDivider: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { emit }) {
    const { $flags } = useContext();
    const { hasFeature } = useFeatures();
    const googleButton = ref(null);
    const {
      loading,
      error,
      useAuthCode,
      prepare,
      initiateLogin,
      renderButton,
    } = useGoogleLogin({
      navigate: props.navigateOnSuccess,
      requestPhone: props.requestPhone,
      onSuccess: () => emit("success"),
    });

    const isEnabled = computed(
      () =>
        hasFeature(FEATURES.GOOGLE_LOGIN) &&
        $flags.isFeatureEnabled("enable-google-oauth")
    );

    onMounted(async () => {
      if (!isEnabled.value) {
        return;
      }
      if (useAuthCode) {
        prepare();
      } else {
        await nextTick();
        renderButton(googleButton.value);
      }
    });

    // Only the authorization-code path uses this custom button; the ID-token
    // path renders Google's own button, so a click here is a no-op there.
    function onButtonClick() {
      if (useAuthCode) {
        initiateLogin();
      }
    }

    return {
      loading,
      error,
      hasFeature,
      FEATURES,
      useAuthCode,
      onButtonClick,
      isEnabled,
      googleButton,
    };
  },
};
</script>
